// src/components/FoodStoreScreen.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import supabaseService from '../services/supabaseService';
import { useFoodCart } from '../store/foodCartState';
import { formatCurrency } from '../utils/helpers';
import {
  FiArrowRight, FiShoppingCart, FiSearch, FiX, FiStar,
  FiPackage, FiTag, FiShoppingBag, FiInbox, FiMapPin, FiChevronDown
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const FoodStoreScreen = () => {
  const navigate = useNavigate();
  const cart = useFoodCart();

  const [allProducts, setAllProducts] = useState([]); // [{service, provider}]
  const [filtered, setFiltered] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedStateId, setSelectedStateId] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statesData, providers] = await Promise.all([
        supabaseService.getStates(),
        supabaseService.getFoodProviders(),
      ]);

      const products = [];
      for (const p of providers) {
        const services = await supabaseService.getServicesForProvider(p.id);
        for (const s of services) {
          if ((s.stock ?? 0) > 0) products.push({ service: s, provider: p });
        }
      }

      setStates(statesData || []);
      setAllProducts(products);
      setFiltered(products);
    } catch (e) {
      console.error('FoodStore load error:', e);
      setError(e.message || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const q = searchQuery.trim();
    setFiltered(
      allProducts.filter((p) => {
        const matchState = selectedStateId === 0 || p.provider.stateId === selectedStateId;
        const matchSearch =
          q === '' ||
          (p.service.name || '').includes(q) ||
          (p.provider.fullName || '').includes(q);
        return matchState && matchSearch;
      })
    );
  }, [searchQuery, selectedStateId, allProducts]);

  const topRated = [...filtered].sort(
    (a, b) => (b.service.averageRating || 0) - (a.service.averageRating || 0)
  );
  const cheapest = [...filtered].sort((a, b) => (a.service.price || 0) - (b.service.price || 0));
  const mostStock = [...filtered].sort((a, b) => (b.service.stock || 0) - (a.service.stock || 0));

  const addToCart = (service, provider) => {
    cart.addItem(service, provider);
    toast.success(`تمت إضافة "${service.name}" إلى السلة`, { duration: 2000 });
  };

  const openDetail = (service, provider) => {
    navigate(`/food-store/product/${service.id}`, { state: { service, provider } });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="text-center">
          <p className="text-gray-900 font-bold mb-2">حدث خطأ</p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button
            onClick={loadData}
            className="bg-gray-900 text-white px-5 py-2 rounded-lg font-bold"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  const Section = ({ icon: Icon, iconColor, title, list }) => (
    <div className="mb-2">
      <div className="flex items-center gap-2 px-4 pt-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: iconColor + '20' }}
        >
          <Icon style={{ color: iconColor }} size={16} />
        </div>
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
      </div>
      <div className="flex gap-3 overflow-x-auto px-4 py-3 scrollbar-hide">
        {list.length === 0 ? (
          <p className="text-gray-400 text-xs py-6">لا توجد مأكولات</p>
        ) : (
          list.map((p) => <ProductCard key={p.service.id} p={p} />)
        )}
      </div>
    </div>
  );

  const ProductCard = ({ p }) => {
    const inCart = cart.items.some((e) => e.service.id === p.service.id);
    const stock = p.service.stock ?? 0;
    return (
      <div
        onClick={() => openDetail(p.service, p.provider)}
        className="flex-shrink-0 w-40 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition"
      >
        <div className="relative h-28 bg-gray-100">
          {p.service.imageUrl ? (
            <img src={p.service.imageUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <FiPackage size={28} />
            </div>
          )}
          {stock <= 5 && (
            <span
              className="absolute top-2 right-2 text-[9px] font-bold text-white px-2 py-0.5 rounded-lg"
              style={{ backgroundColor: stock === 0 ? '#ef4444' : '#f97316' }}
            >
              {stock === 0 ? 'نفذت الكمية' : `آخر ${stock}`}
            </span>
          )}
        </div>
        <div className="p-2.5">
          <p className="text-xs font-bold text-gray-900 line-clamp-2 mb-1">{p.service.name}</p>
          <p className="text-[10px] text-gray-400 truncate mb-1.5">{p.provider.fullName}</p>
          <div className="flex items-center gap-1 mb-2">
            <FiStar className="text-yellow-400" size={11} />
            <span className="text-[10px] text-gray-500">
              {(p.service.averageRating || 0) > 0 ? p.service.averageRating.toFixed(1) : 'جديد'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-black text-gray-900">
              {formatCurrency ? formatCurrency(p.service.price || 0) : `${p.service.price} د.ج`}
            </span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              stock > 0 && addToCart(p.service, p.provider);
            }}
            disabled={stock === 0}
            className={`w-full mt-2 py-1.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1 transition ${
              stock === 0
                ? 'bg-gray-100 text-gray-400'
                : inCart
                ? 'bg-green-50 text-green-600 border border-green-500'
                : 'bg-red-600 text-white'
            }`}
          >
            <FiShoppingCart size={12} />
            {stock === 0 ? 'نفذ' : inCart ? 'في السلة' : 'أضف للسلة'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-gray-100">
            <FiArrowRight />
          </button>
          <h1 className="font-bold text-gray-900 flex items-center gap-2">
            <FiShoppingBag className="text-red-600" /> khidma food
          </h1>
          <button
            onClick={() => navigate('/food-store/cart')}
            className="relative p-2 rounded-lg bg-gray-100"
          >
            <FiShoppingCart />
            {cart.totalCount > 0 && (
              <span className="absolute -top-1 -left-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                {cart.totalCount}
              </span>
            )}
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pb-2">
          <div className="relative">
            <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ابحث عن وجبة أو مطعم..."
              className="w-full bg-gray-100 rounded-lg pr-9 pl-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/20"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              >
                <FiX size={14} />
              </button>
            )}
          </div>
        </div>

        {/* State/Province Filter */}
        <div className="px-4 pb-3 flex items-center gap-2">
          <div className="relative flex-1">
            <FiMapPin className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <select
              value={selectedStateId}
              onChange={(e) => setSelectedStateId(parseInt(e.target.value) || 0)}
              className="w-full bg-gray-100 border border-gray-200 rounded-lg pr-9 pl-8 py-2.5 text-xs font-bold text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-red-600/20 cursor-pointer"
            >
              <option value={0}>كل الولايات (الكل)</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <FiChevronDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Content */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <FiInbox size={48} className="mb-3 opacity-40" />
          <p className="text-sm font-bold">لا توجد وجبات أو مطاعم مطابقة</p>
        </div>
      ) : (
        <div className="pb-8">
          <Section icon={FiStar} iconColor="#FFCC00" title="الأعلى تقييماً" list={topRated} />
          <div className="border-b border-gray-100 mx-4" />
          <Section icon={FiTag} iconColor="#22c55e" title="الأقل سعراً" list={cheapest} />
          <div className="border-b border-gray-100 mx-4" />
          <Section icon={FiPackage} iconColor="#3b82f6" title="الأوفر مخزوناً" list={mostStock} />
        </div>
      )}
    </div>
  );
};

export default FoodStoreScreen;
